import { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { canonical, webPageSchema } from "@/utils/seo";

export default function PrivacyPage() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="w-full">
      <SEO
        meta={{
          title: "隱私權政策｜美好時光指南",
          description: "美好時光指南隱私權政策，說明我們如何收集、使用與保護你的個人資料。",
          canonical: canonical("/privacy"),
          keywords: ["隱私權政策", "個人資料", "美好時光指南"],
          structuredData: webPageSchema("隱私權政策｜美好時光指南", "美好時光指南隱私權政策。", "/privacy"),
        }}
      />

      {/* Header */}
      <div className="w-full bg-slate-50 border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
            隱私權政策
          </h1>
          <p className="text-slate-500">
            最後更新日期：2025 年 5 月 14 日
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div
          className={`space-y-10 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">
              1. 我們收集哪些資料
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              當你使用美好時光指南（以下簡稱「本平台」）時，我們可能收集以下類型的資料：
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-slate-600 leading-relaxed">
              <li>
                <strong className="text-slate-700">基本帳號資訊</strong>
                ：姓名、電子郵件地址、手機號碼（當你註冊會員時）
              </li>
              <li>
                <strong className="text-slate-700">使用紀錄</strong>
                ：瀏覽過的文章、搜尋關鍵字、點擊的服務頁面
              </li>
              <li>
                <strong className="text-slate-700">裝置資訊</strong>
                ：IP 位址、瀏覽器類型、作業系統、螢幕解析度
              </li>
              <li>
                <strong className="text-slate-700">位置資訊</strong>
                ：若你授權，我們可能收集你的 GPS 位置以提供附近店家推薦
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">
              2. 我們如何使用你的資料
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              我們使用收集到的資料來：
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-slate-600 leading-relaxed">
              <li>提供、維護與改善本平台的功能與服務</li>
              <li>個人化推薦內容，讓你看到更相關的文章與活動</li>
              <li>寄送會員通知、電子報與行銷資訊（你可以隨時退訂）</li>
              <li>分析使用者行為，優化網站體驗</li>
              <li>處理你的客服請求與平台互動</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">
              3. 資料分享與揭露
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              我們不會出售你的個人資料。但在以下情況，我們可能與第三方分享資料：
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-slate-600 leading-relaxed mt-3">
              <li>
                <strong className="text-slate-700">合作夥伴</strong>
                ：當你透過本平台與店家互動時，我們可能將必要的資料傳遞給該合作店家
              </li>
              <li>
                <strong className="text-slate-700">法律要求</strong>
                ：當法院、政府機關依法要求時
              </li>
              <li>
                <strong className="text-slate-700">網站分析</strong>
                ：我們使用 Google Analytics 等工具分析流量，這些工具可能收集匿名化的使用數據
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">
              4. Cookie 與類似技術
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              我們使用 Cookie 來記住你的登入狀態、儲存偏好設定，並追蹤網站使用情況。你可以透過瀏覽器設定拒絕 Cookie，但部分功能可能因此無法正常使用。
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">
              5. 資料安全
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              我們採取合理的技術與組織措施保護你的個人資料，包括加密傳輸（SSL）、資料庫存取控管、定期安全審查。然而，沒有任何網路傳輸或電子儲存方式是 100% 安全的，我們無法保證絕對安全。
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">
              6. 你的權利
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              依據個人資料保護法，你享有以下權利：
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-slate-600 leading-relaxed">
              <li>查詢或請求閱覽你的個人資料</li>
              <li>請求製給複製本</li>
              <li>請求補充或更正資料</li>
              <li>請求停止收集、處理或利用</li>
              <li>請求刪除資料</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-800 mb-3">
              7. 聯絡我們
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              若對本隱私權政策有任何疑問，請透過以下方式聯絡我們：
            </p>
            <p className="text-sm text-slate-600 leading-relaxed mt-2">
              電子郵件：privacy@goldentimeguide.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}